/**
 * Color por ítem de lista (`List.values[].color`).
 *
 * Lo asigna el admin desde el editor de listas (modal «Editar elemento») y se usa como
 * fondo del badge donde se muestre ese valor, en cualquier listado. Aplica a todas las
 * listas, no solo a la de banderitas (`flag_category`).
 *
 * Dos decisiones de producto viven acá:
 * - La paleta es **cerrada** (`LIST_ITEM_COLOR_PRESETS`): sin rueda RGB libre, para que
 *   los ítems compartan una escala de colores y no termine cada uno con un tono propio.
 * - Sin color asignado el badge queda **neutro**: el color es una decisión explícita del
 *   admin, no algo derivado del nombre del ítem.
 */

/** Paleta cerrada del selector de color de ítems. */
export const LIST_ITEM_COLOR_PRESETS = [
  "#9e9e9e",
  "#64748b",
  "#4fc3f7",
  "#29b6f6",
  "#039be5",
  "#2563eb",
  "#66bb6a",
  "#22c55e",
  "#0f766e",
  "#f1cd4b",
  "#efc21f",
  "#f6ab1a",
  "#f97316",
  "#e11d48",
  "#dc2626",
  "#db2777",
  "#7c3aed",
  "#5c2d91",
  "#e8daf5",
  "#fde7e9",
];

/** Badge de un ítem sin color asignado. */
export const LIST_ITEM_NEUTRAL_BADGE_STYLE = Object.freeze({
  backgroundColor: "#e7e7e7",
  color: "#323130",
  borderColor: "#323130",
});

export function normalizeHexColor(raw) {
  if (raw == null || raw === "") return "";
  const s = String(raw).trim();
  if (/^#[0-9a-fA-F]{6}$/.test(s)) return s.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(s)) {
    const r = s[1];
    const g = s[2];
    const b = s[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return "";
}

/** Texto legible (negro/blanco) sobre un fondo hexadecimal. */
export function contrastTextForBg(hex) {
  const c = normalizeHexColor(hex);
  if (!c) return "#323130";
  const r = parseInt(c.slice(1, 3), 16);
  const g = parseInt(c.slice(3, 5), 16);
  const b = parseInt(c.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.62 ? "#1a1a1a" : "#ffffff";
}

export function hexToRgba(hex, alpha) {
  const c = normalizeHexColor(hex);
  if (!c) return "";
  const r = parseInt(c.slice(1, 3), 16);
  const g = parseInt(c.slice(3, 5), 16);
  const b = parseInt(c.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Estilo del badge para un color guardado. Sin color válido devuelve el neutro, así que
 * el valor siempre se lee (nunca queda texto sin fondo).
 */
export function getListItemBadgeStyle(color) {
  const bg = normalizeHexColor(color);
  if (!bg) return { ...LIST_ITEM_NEUTRAL_BADGE_STYLE };
  const fg = contrastTextForBg(bg);
  return { backgroundColor: bg, color: fg, borderColor: fg };
}

/** Id de un valor de lista, venga poblado (`{_id,label}`) o como id/etiqueta suelta. */
function listValueKey(value) {
  if (value == null) return "";
  if (typeof value === "object") {
    return String(value._id || value.id || value.label || "").trim();
  }
  return String(value).trim();
}

/**
 * Color de un valor de lista buscándolo en los ítems de la lista (por _id o etiqueta).
 * @param {*} value valor guardado en el documento
 * @param {Array<{_id?:string,id?:string,label?:string,color?:string}>} listItems
 * @returns {string} hex normalizado, o '' si el ítem no tiene color
 */
export function resolveListItemColor(value, listItems) {
  const key = listValueKey(value);
  if (!key || !Array.isArray(listItems) || !listItems.length) return "";
  const keyLower = key.toLowerCase();
  const item = listItems.find((opt) => {
    if (!opt) return false;
    const id = opt._id || opt.id;
    if (id && String(id) === key) return true;
    return String(opt.label || "").trim().toLowerCase() === keyLower;
  });
  return normalizeHexColor(item?.color);
}

/** Igual que `resolveListItemColor`, pero devuelve el estilo del badge (o `null`). */
export function getListValueBadgeStyle(value, listItems) {
  const color = resolveListItemColor(value, listItems);
  return color ? getListItemBadgeStyle(color) : null;
}

function escapeHtml(value) {
  return String(value == null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Grilla de swatches + opción «sin color» para meter dentro del HTML de un Swal.
 * El color actual se marca aunque no esté en la paleta (los flags cargados antes de la
 * paleta cerrada conservan su tono hasta que el admin elija otro).
 */
export function buildListItemColorPickerHtml({
  selected = "",
  noneLabel = "No color",
  inputId = "swal-item-color",
} = {}) {
  const current = normalizeHexColor(selected);
  const swatches = LIST_ITEM_COLOR_PRESETS.map(
    (hex) =>
      `<button type="button" class="swal-list-color-swatch${
        hex === current ? " is-selected" : ""
      }" data-color="${hex}" style="background:${hex};" title="${hex}"></button>`
  ).join("");
  const customSwatch =
    current && !LIST_ITEM_COLOR_PRESETS.includes(current)
      ? `<button type="button" class="swal-list-color-swatch is-selected" data-color="${current}" style="background:${current};" title="${current}"></button>`
      : "";
  return `
    <style>
      .swal-list-color-picker { display: flex; flex-wrap: wrap; gap: 0.35rem; align-items: center; }
      .swal-list-color-swatch { width: 1.5rem; height: 1.5rem; border-radius: 0.25rem; border: 2px solid transparent; padding: 0; cursor: pointer; }
      .swal-list-color-swatch.is-selected { box-shadow: 0 0 0 2px #fff, 0 0 0 4px #0d6efd; }
      .swal-list-color-none { border: 1px solid #adb5bd; background: transparent; border-radius: 0.25rem; padding: 0.1rem 0.5rem; font-size: 0.8rem; cursor: pointer; }
      .swal-list-color-none.is-selected { box-shadow: 0 0 0 2px #fff, 0 0 0 4px #0d6efd; }
    </style>
    <input id="${inputId}" type="hidden" value="${current}" />
    <div class="swal-list-color-picker">
      ${swatches}${customSwatch}
      <button type="button" class="swal-list-color-none${
        current ? "" : " is-selected"
      }" data-color="">${escapeHtml(noneLabel)}</button>
    </div>
  `;
}

/**
 * Cablea la grilla de `buildListItemColorPickerHtml` dentro del `didOpen` de un Swal:
 * cada swatch escribe su hex en el input oculto y queda marcado.
 */
export function bindListItemColorPicker(popup, inputId = "swal-item-color") {
  if (!popup) return;
  const input = popup.querySelector(`#${inputId}`);
  const buttons = popup.querySelectorAll(
    ".swal-list-color-swatch, .swal-list-color-none"
  );
  buttons.forEach((btn) => {
    btn.addEventListener("click", (ev) => {
      ev.preventDefault();
      const hex = normalizeHexColor(btn.getAttribute("data-color"));
      if (input) input.value = hex;
      buttons.forEach((other) => other.classList.remove("is-selected"));
      btn.classList.add("is-selected");
    });
  });
}
